import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Bot, User, Sparkles } from 'lucide-react';
import { findBestResponse, quickReplies } from '../../utils/chatPatterns';
import './WhatsAppWidget.css';

interface Message {
    id: string;
    text: string;
    sender: 'user' | 'bot';
    timestamp: Date;
}

export const WhatsAppWidget: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [messages, setMessages] = useState<Message[]>([
        {
            id: '1',
            text: 'Merhaba! 👋 Ben Cheesecake İstanbul AI asistanınızım.\n\nSize şu konularda yardımcı olabilirim:\n\n📦 Sipariş takibi\n\n🍰 Ürün önerileri\n\n🎁 Numune talebi\n\n💳 Ödeme & Teslimat\n\n📞 İletişim bilgileri\n\nNasıl yardımcı olabilirim?',
            sender: 'bot',
            timestamp: new Date()
        }
    ]);
    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isTyping]);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    const handleSendMessage = async (text?: string) => {
        const messageText = text || message.trim();
        if (!messageText) return;

        // Kullanıcı mesajını ekle
        const userMessage: Message = {
            id: Date.now().toString(),
            text: messageText,
            sender: 'user',
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setMessage('');

        // Bot yazıyor animasyonu
        setIsTyping(true);

        // Gerçekçi gecikme (500-1500ms arası)
        const delay = 500 + Math.random() * 1000;
        
        setTimeout(() => {
            // AI yanıtını al
            const botResponse = findBestResponse(messageText);

            const botMessage: Message = {
                id: (Date.now() + 1).toString(),
                text: botResponse,
                sender: 'bot',
                timestamp: new Date()
            };

            setMessages(prev => [...prev, botMessage]);
            setIsTyping(false);
        }, delay);
    };

    const handleQuickReply = (text: string) => {
        handleSendMessage(text);
    };

    const formatTime = (date: Date) => {
        return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <>
            {/* Floating Button */}
            <button
                className={`ai-chat-fab ${isOpen ? 'open' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
                aria-label="AI Asistan"
            >
                {isOpen ? <X size={24} /> : <MessageCircle size={24} />}
                {!isOpen && <span className="pulse-dot"></span>}
            </button>

            {/* Chat Window */}
            {isOpen && (
                <div className="ai-chat-window">
                    <div className="chat-header">
                        <div className="avatar">
                            <Bot size={24} />
                        </div>
                        <div className="info">
                            <h4>
                                <Sparkles size={14} />
                                AI Asistan
                            </h4>
                            <p><span className="status-dot"></span> Çevrimiçi</p>
                        </div>
                        <button className="close-btn" onClick={() => setIsOpen(false)}>
                            <X size={20} />
                        </button>
                    </div>

                    <div className="chat-body">
                        {messages.map((msg) => (
                            <div
                                key={msg.id}
                                className={`message ${msg.sender === 'user' ? 'sent' : 'received'}`}
                            >
                                <div className="message-avatar">
                                    {msg.sender === 'bot' ? <Bot size={16} /> : <User size={16} />}
                                </div>
                                <div className="message-content">
                                    <p style={{ whiteSpace: 'pre-line' }}>{msg.text}</p>
                                    <span className="time">{formatTime(msg.timestamp)}</span>
                                </div>
                            </div>
                        ))}

                        {isTyping && (
                            <div className="message received typing">
                                <div className="message-avatar">
                                    <Bot size={16} />
                                </div>
                                <div className="message-content">
                                    <div className="typing-indicator">
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div ref={messagesEndRef} />
                    </div>

                    {/* Quick Replies */}
                    {messages.length <= 2 && !isTyping && (
                        <div className="quick-replies">
                            {quickReplies.map((reply, index) => (
                                <button
                                    key={index}
                                    onClick={() => handleQuickReply(reply.text)}
                                    className="quick-reply-btn"
                                >
                                    <span className="icon">{reply.icon}</span>
                                    {reply.text}
                                </button>
                            ))}
                        </div>
                    )}

                    <div className="chat-footer">
                        <input
                            ref={inputRef}
                            type="text"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Mesajınızı yazın..."
                            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                            disabled={isTyping}
                        />
                        <button
                            className="send-btn"
                            onClick={() => handleSendMessage()}
                            disabled={!message.trim() || isTyping}
                        >
                            <Send size={20} />
                        </button>
                    </div>

                    <div className="chat-footer-info">
                        <Sparkles size={14} />
                        <span>AI destekli akıllı asistan</span>
                    </div>
                </div>
            )}
        </>
    );
};
